/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import { CircularProgress } from '@mui/material'
import { Fragment, ReactElement, useCallback, useEffect, useState } from 'react';
import ComponentError from './ComponentError'
import RequestHandler from '../../../../lib/request_handler'
import Article from '../../../Commons/Article'
import { FeaturedArticle, FeaturedArticleComponentProps } from '../../../types'
import routes from '../../../utils/routes'

type Props = {
  article: FeaturedArticle
  debouncedBody: string
  watchBody: string
  onError: () => void
}

type PreviewResponse = {
  body: {
    components: FeaturedArticleComponentProps
  }
  error?: string
}

function PreviewComponent(props: Props): ReactElement {
  const article = props.article
  const watchBody = props.watchBody
  const debouncedBody = props.debouncedBody
  const onError = props.onError
  const [error, setError] = useState('')
  const [components, setComponents] = useState<FeaturedArticleComponentProps>([])
  const [loadingPreview, setLoadingPreview] = useState(false)

  const drawPreview = useCallback(async (): Promise<void> => {
    const handler = new RequestHandler(
      routes.staff_admin_featured_article_content_preview_path(article.id),
      'post',
      { body: debouncedBody }
    )
    try {
      const response = (await handler.requestWithBody()) as PreviewResponse
      setLoadingPreview(false)
      if (response.body.components) {
        setComponents(response.body.components)
        setError('')
      }
    } catch (error) {
      onError()

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const response = error.response.data as PreviewResponse
      setComponents([])
      setLoadingPreview(false)
      setError(response.error)
    }
  }, [article.id, debouncedBody, onError])

  useEffect(() => {
    void drawPreview()
  }, [drawPreview])

  useEffect(() => setLoadingPreview(true), [watchBody])

  return (
    <Fragment>
      <div className="preview_component" css={previewComponentStyle}>
        <div css={articleWrapperStyle}>
          <Article article={article} components={components} />
          {error && <ComponentError>{error}</ComponentError>}
        </div>
        {loadingPreview && (
          <CircularProgress
            sx={{
              position: 'absolute',
              margin: 'auto',
              display: 'block',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        )}
      </div>
    </Fragment>
  )
}

const previewComponentStyle = css`
  width: 480px;
  min-height: calc(100vh - 96px);
  max-height: calc(100vh - 96px);
  position: relative;
`

const articleWrapperStyle = css`
  width: 480px;
  min-height: calc(100vh - 96px);
  max-height: calc(100vh - 96px);
  overflow-y: scroll;
`

export default PreviewComponent
